<template>
  <v-container>
    <h1 class="pb-4 primary--text font-weight-bold">
      {{ isEdit ? $t("landlord.editProperty") : $t("landlord.addProperty") }}
    </h1>
    <ValidationObserver ref="observer" v-slot="{ invalid }">
      <v-form :key="rerender" ref="form">
        <!-- select landlord manually if from all properties page -->
        <ValidationProvider
          v-if="manualLandlordSelection"
          :rules="{
            required: true
          }"
          :name="$t('core.selectLandlord')"
          v-slot="{ errors }"
        >
          <v-autocomplete
            :loading="$apollo.queries.getLandlords.loading"
            :items="landlordLists"
            :search-input.sync="landlordSearchKey"
            :label="$t('core.selectLandlord')"
            item-value="_id"
            item-text="user.firstName"
            v-model="selectedLandlordId"
            :error-messages="errors"
            outlined
            dense
          >
          </v-autocomplete>
        </ValidationProvider>
        <!-- property details card -->
        <v-card :loading="$apollo.queries.getPropertyById.loading">
          <v-card-title>
            <span class="px-3 text-body-1 font-weight-medium">
              {{ $t("landlord.propertyDetails") }}
            </span>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col
                class="py-0"
                v-for="(field, index) in propertyDetailsCard"
                :cols="field.colAttrs.cols ? field.colAttrs.cols : 6"
                v-bind="field.colAttrs"
                :key="index"
              >
                <ValidationProvider
                  v-if="field.otherConditions"
                  :rules="{
                    ...{ required: field.type !== 'editor' },
                    ...field.validationAttrs
                  }"
                  v-slot="{ errors }"
                  :name="
                    field.attrs.label ? field.attrs.label : field.labelText
                  "
                >
                  <v-checkbox
                    v-if="field.type === 'checkbox'"
                    :error-messages="errors"
                    hide-details="auto"
                    v-bind="field.attrs"
                    v-model="variables[field.modelMap]"
                  >
                  </v-checkbox>
                  <v-select
                    :error-messages="errors"
                    v-if="field.type === 'select'"
                    outlined
                    dense
                    v-bind="field.attrs"
                    v-model="variables[field.modelMap]"
                  >
                    <template #label>
                      <span class="red--text"><strong>* </strong></span>
                      <span>{{ field.attrs.label }}</span>
                    </template>
                  </v-select>
                  <v-text-field
                    :error-messages="errors"
                    v-if="field.type === 'text'"
                    outlined
                    dense
                    v-bind="field.attrs"
                    v-model="variables[field.modelMap]"
                  >
                    <template #label>
                      <span class="red--text"><strong>* </strong></span>
                      <span>{{ field.attrs.label }}</span>
                    </template>
                  </v-text-field>
                  <v-textarea
                    :error-messages="errors"
                    v-if="field.type === 'textArea'"
                    v-model="variables[field.modelMap]"
                    v-bind="field.attrs"
                    outlined
                    no-resize
                  >
                    <template #label>
                      <span class="red--text"><strong>* </strong></span>
                      <span>{{ field.attrs.label }}</span>
                    </template>
                  </v-textarea>
                  <div class="py-2" v-if="field.type === 'editor'">
                    <label>
                      <span class="red--text"><strong>* </strong></span>
                      {{ field.labelText }}
                    </label>
                    <ckeditor
                      :config="ckeditorConfig"
                      v-model="variables[field.modelMap]"
                    />
                  </div>
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <!-- location details -->
        <v-card>
          <v-card-title>
            <span class="px-3 text-body-1 font-weight-medium">
              {{ $t("landlord.locationDetails") }}
            </span>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col
                v-for="(field, index) in propertyLocationCard"
                :key="index"
                :cols="field.colAttrs.cols ? field.colAttrs.cols : 6"
              >
                <SelectLocation
                  v-bind="field.attrs"
                  v-on="field.on"
                  v-if="field.type === 'map'"
                />
                <ValidationProvider
                  v-slot="{ errors }"
                  rules="required"
                  :name="field.attrs.label"
                >
                  <v-select
                    v-if="field.type === 'select'"
                    :error-messages="errors"
                    outlined
                    dense
                    v-bind="field.attrs"
                    v-model="locationVariables[field.modelMap]"
                  >
                    <template #label>
                      <span class="red--text"><strong>* </strong></span>
                      <span>{{ field.attrs.label }}</span>
                    </template>
                  </v-select>
                  <v-textarea
                    v-if="field.type === 'textArea'"
                    :error-messages="errors"
                    outlined
                    no-resize
                    v-bind="field.attrs"
                    v-model="locationVariables[field.modelMap]"
                  ></v-textarea>
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <!-- property images card -->
        <v-row class="tw-mt-4" v-for="(card, index) in propertyImageCards" :key="index">
          <v-col cols="6">
            <v-card
              v-on="card.on"
              :height="card.cardHeight"
              class="justify-center py-10 rounded-lg card-custom-border d-flex align-center"
            >
              <v-card-text class="text-center">
                <ValidationProvider
                  :rules="{
                    required: true,
                    maxSize: 8000
                  }"
                >
                  <v-file-input
                    :id="card.modelMap"
                    v-bind="card.attrs"
                    v-model="propertyImageVariables[card.modelMap]"
                    v-show="false"
                    accept="image/*"
                    label="File input"
                  ></v-file-input>
                </ValidationProvider>
                <v-img
                  class="mx-auto"
                  :src="require('@/assets/images/cameraUpload.png')"
                  width="50px"
                ></v-img>
                <div>
                  <p class="mt-5">
                    <span class="red--text"><strong>* </strong></span>
                    {{ card.text }}
                  </p>
                  <div class="d-flex flex-column" style="gap: 5px;">
                    <v-btn
                      class="mx-auto rounded-lg white--text"
                      color="primary"
                      height="40px"
                      elevation="0"
                      width="200px"
                    >
                      {{ $t("landlord.addPhotos") }}
                    </v-btn>
                    <span class="mx-auto red--text text-caption">
                      *{{ $t("inputRules.fileLengthWarning") }}
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-col
              class="py-0"
              :cols="6"
              v-for="(image, index) in card.images"
              :key="index"
            >
              <v-hover v-slot="{ hover }">
                <v-card
                  v-on="card.imageCardOn"
                  flat
                  height="175"
                  class="rounded-lg cursor-pointer "
                >
                  <v-img height="100%" width="100%" :src="image"> </v-img>
                  <v-fade-transition>
                    <v-overlay v-if="hover" absolute color="primary">
                      <v-icon>mdi-close</v-icon>
                    </v-overlay>
                  </v-fade-transition>
                </v-card>
              </v-hover>
            </v-col>
          </v-col>
        </v-row>
        <div class=" tw-flex tw-w-full tw-gap-2 tw-justify-end">
          <div class="">
            <v-btn
                width="120"
                class="black--text"
                color="#FFFFFFFF"
                outlined
              @click="goBack"
            >
              {{ $t("common.cancel") }}
            </v-btn>
          </div>
          <div class="">
            <v-btn
               width="120"
              :loading="btnLoading"
              :disabled="invalid && editorsOccupied"
              @click="onSave"
              class="white--text"
              color="primary"
            >
              {{ isEdit ? "Update" : "Submit" }}
            </v-btn>
          </div>
        </div>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import "@/core/methods/validation_rules";
export default {
  components: {
    SelectLocation: () =>
      import("@/landlord/components/LocationSelectDialog.vue")
  },
  props: {
    isEdit: {
      type: Boolean
    },
    manualLandlordSelection: {
      type: Boolean
    },
    landlordId: {
      type: String
    },
    propertyId: {
      type: String
    }
  },
  data() {
    return {
      variables: {
        propertyType: null,
        rentType: "PER_MONTH",
        nameEn: null,
        nameAr: null,
        latitude: null,
        longitude: null,
        descriptionEn: null,
        descriptionAr: null,
        policiesEn: null,
        policiesAr: null,
        helthHygeineEn: null,
        helthHygeineAr: null,
        facilities: null,
        images: null,
        thumbanails: null,
        landmark: null,
        isTaxInclusive: null,
        tax: null,
        currency: null,
        isCurrentAndWaterBillIncluded: null
      },
      locationVariables: {
        area: null,
        block: null,
        addressLine1: null,
        addressLine2: null,
        landmark: null
      },
      propertyImageVariables: {
        thumbnail: [],
        images: []
      },
      propertyTypes: [],
      facilities: [],
      currencyTypes: [],
      areas: [],
      blocks: [],
      landmarks: [],
      landlordLists: [],
      selectedLandlordId: null,
      landlordSearchKey: null,
      rerender: 0,
      btnLoading: false
    };
  },
  watch: {
    propertyDetails: {
      handler(val) {
        this.variables.policiesEn = val.policiesEn;
        this.variables.policiesAr = val.policiesAr;
        this.variables.helthHygeineEn = val.helthHygeineEn;
        this.variables.helthHygeineAr = val.helthHygeineAr;
      },
      deep: true
    }
  },
  methods: {
    goBack() {
      this.$emit("refetchProperties");
      this.$store.commit("SHOW_RIGHTSIDE_DRAWER", false);
      if (!this.isEdit) {
        this.$refs.form.reset();
        this.$refs.form.resetValidation();
      }
    },
    setLatLng(center) {
      this.locationError = null;
      this.variables.latitude = center.lat;
      this.variables.longitude = center.lng;
    },
    onSave() {
      const variables = {
        input: {
          nameEn: this.variables.nameEn,
          nameAr: this.variables.nameAr,
          propertyType: this.variables.propertyType,
          address: {
            area: this.locationVariables.area,
            block: this.locationVariables.block,
            addressLine1: this.locationVariables.addressLine1,
            addressLine2: this.locationVariables.addressLine2,
            location: {
              latitude: this.variables.latitude,
              longitude: this.variables.longitude
            }
          },
          location: {
            latitude: this.variables.latitude,
            longitude: this.variables.longitude
          },
          descriptionEn: this.variables.descriptionEn,
          descriptionAr: this.variables.descriptionAr,
          facilities: this.variables.facilities,
          policiesEn: this.variables.policiesEn,
          policiesAr: this.variables.policiesAr,
          helthHygeineEn: this.variables.helthHygeineEn,
          helthHygeineAr: this.variables.helthHygeineAr,
          images:
            typeof this.propertyImageVariables.images[0] === "string"
              ? null
              : this.propertyImageVariables.images,
          thumbanails:
            typeof this.propertyImageVariables.thumbnail[0] === "string"
              ? null
              : this.propertyImageVariables.thumbnail,
          tax: parseFloat(this.variables.tax),
          isTaxInclusive: this.variables.isTaxInclusive,
          rentType: this.variables.rentType,
          landmark: this.locationVariables.landmark,
          currency: this.variables.currency,
          latitude: this.variables.latitude,
          longitude: this.variables.longitude,
          isCurrentAndWaterBillIncluded: this.variables
            .isCurrentAndWaterBillIncluded,
          landlordId: this.manualLandlordSelection
            ? this.selectedLandlordId
            : this.landlordId
        }
      };
      if (this.isEdit) {
        this.btnLoading = true;
        this.$apollo
          .mutate({
            mutation: require("@/property/api/mutations/property/update_property.graphql"),
            variables: {
              ...{ id: this.propertyId },
              ...variables
            }
          })
          .then(({ data }) => {
            if (data.updateProperty._id) {
              this.btnLoading = false;
              this.$root.$emit("snackbar", {
                snackbar: true,
                text: this.$t("common.saveSuccess"),
                color: "primary"
              });

              this.$store.commit("SHOW_RIGHTSIDE_DRAWER", false);
              this.$emit("refetchProperties");
            } else {
              this.$root.$emit("snackbar", {
                snackbar: true,
                text: "Error Occured",
                color: "error"
              });
              this.btnLoading = false;
              this.$store.commit("SHOW_RIGHTSIDE_DRAWER", false);
            }
          });
      } else {
        this.btnLoading = true;
        this.$apollo
          .mutate({
            mutation: require("@/property/api/mutations/property/create_property.graphql"),
            variables: variables
          })
          .then(({ data }) => {
            this.btnLoading = false;
            if (data.createProperty._id) {
              this.$root.$emit("snackbar", {
                snackbar: true,
                text: this.$t("common.saveSuccess"),
                color: "primary"
              });
              this.$store.commit("SHOW_RIGHTSIDE_DRAWER", false);
              this.$emit("refetchProperties");
              this.$refs.form.reset();
              this.$refs.form.resetValidation();
            } else {
              this.$root.$emit("snackbar", {
                snackbar: true,
                text: "Error Occured",
                color: "error"
              });
              this.$store.commit("SHOW_RIGHTSIDE_DRAWER", false);
            }
          });
      }
    }
  },
  computed: {
    editorsOccupied() {
      return (
        this.variables.policiesEn &&
        this.variables.policiesAr &&
        this.variables.helthHygeineEn &&
        this.variables.helthHygeineAr
      );
    },
    viewOnlyImages() {
      let propertyImages = [];
      let propertyThumbnail = [];
      if (this.propertyImageVariables.thumbnail[0]) {
        if (typeof this.propertyImageVariables.thumbnail[0] === "string") {
          propertyThumbnail.push(this.propertyImageVariables.thumbnail[0]);
        } else {
          propertyThumbnail.push(
            URL.createObjectURL(this.propertyImageVariables.thumbnail[0])
          );
        }
      } else {
        propertyThumbnail = [];
      }
      if (this.propertyImageVariables.images.length > 0) {
        this.propertyImageVariables.images.forEach(image => {
          if (typeof image === "string") {
            propertyImages.push(image);
          } else {
            propertyImages.push(URL.createObjectURL(image));
          }
        });
      } else {
        propertyImages = [];
      }
      return {
        propertyImages: propertyImages,
        propertyThumbnail: propertyThumbnail
      };
    },
    ckeditorConfig() {
      return {
        toolbar: [
          {
            name: "basicstyles",
            items: [
              "Bold",
              "Italic",
              "Underline",
              "Strike",
              "Subscript",
              "Superscript",
              "RemoveFormat"
            ]
          },
          { name: "lists", items: ["NumberedList", "BulletedList"] },
          {
            name: "styles",
            items: ["Styles", "Format", "Font", "FontSize"]
          },
          { name: "colors", items: ["TextColor", "BGColor"] }
        ]
      };
    },
    propertyDetailsCard() {
      return [
        {
          type: "select",
          attrs: {
            items: [
              {
                text: "MONTHLY",
                value: "PER_MONTH"
              },
              {
                text: "DAILY",
                value: "PER_DAY"
              }
            ],
            "item-text": "text",
            "item-value": "value",
            disabled: true,
            label: "Rent Type"
          },
          modelMap: "rentType",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "select",
          attrs: {
            items: this.propertyTypes,
            "item-text": "nameEn",
            "item-value": "_id",
            label: "Property Type",
            disabled: !this.variables.rentType
          },
          modelMap: "propertyType",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "text",
          attrs: {
            label: this.$t("landlord.propertyNameEn")
          },
          modelMap: "nameEn",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "text",
          attrs: {
            label: this.$t("landlord.propertyNameAr")
          },
          modelMap: "nameAr",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "textArea",
          attrs: {
            label: "Tell About This Property in English"
          },
          modelMap: "descriptionEn",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "textArea",
          attrs: {
            label: "Tell About This Property in Arabic"
          },
          modelMap: "descriptionAr",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "editor",
          labelText: "Health & Hygiene policies in English",
          attrs: {},
          modelMap: "helthHygeineEn",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "editor",
          labelText: "Health & Hygiene policies in Arabic",
          attrs: {},
          modelMap: "helthHygeineAr",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "editor",
          labelText: "Policies in English",
          attrs: {},
          modelMap: "policiesEn",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "editor",
          labelText: "Policies in Arabic",
          attrs: {},
          modelMap: "policiesAr",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "select",
          attrs: {
            label: "Facilities",
            multiple: true,
            items: this.facilities,
            "item-text": "nameEn",
            "item-value": "_id"
          },
          modelMap: "facilities",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "select",
          attrs: {
            label: "Select Currency Type",
            items: this.currencyTypes,
            "item-text": "currency",
            "item-value": "_id"
          },
          modelMap: "currency",
          colAttrs: {},
          validationAttrs: {}
        },
        {
          type: "checkbox",
          attrs: {
            class: "mt-0",
            label: "Current and water bill included?"
          },
          modelMap: "isCurrentAndWaterBillIncluded",
          colAttrs: {
            cols: 12
          },
          validationAttrs: {}
        },
        {
          type: "checkbox",
          attrs: {
            class: "",
            label: "Is rent tax inclusive ?"
          },
          modelMap: "isTaxInclusive",
          colAttrs: {
            cols: 12
          },
          validationAttrs: {}
        },
        {
          type: "text",
          otherConditions: !this.variables.isTaxInclusive,
          attrs: {
            label: "Tax",
            class: "pt-3",
            "append-outer-icon": "mdi-percent",
            disabled: this.variables.isTaxInclusive
          },
          modelMap: "tax",
          colAttrs: {},
          validationAttrs: {
            required: !this.variables.isTaxInclusive,
            regex: /^[0-9]{1,3}(?:\.[0-9]{1,3})?$/,
            maxValue: 100
          }
        }
      ].map(field => {
        field.otherConditions =
          field.otherConditions !== undefined ? field.otherConditions : true;
        return field;
      });
    },
    propertyLocationCard() {
      return [
        {
          type: "map",
          attrs: {
            latLng: {
              latitude: this.variables.latitude,
              longitude: this.variables.longitude
            }
          },
          colAttrs: {},
          on: {
            setLatLng: center => this.setLatLng(center)
          }
        },
        {
          type: "select",
          attrs: {
            label: this.$t("landlord.area"),
            items: this.areas,
            "item-text": "nameEn",
            "item-value": "_id"
          },
          modelMap: "area",
          colAttrs: {}
        },
        {
          type: "select",
          attrs: {
            label: this.$t("landlord.block"),
            items: this.blocks,
            "item-text": "nameEn",
            "item-value": "_id"
          },
          modelMap: "block",
          colAttrs: {}
        },
        {
          type: "select",
          attrs: {
            label: this.$t("settings.landmark.title"),
            items: this.landmarks,
            "item-text": "nameEn",
            "item-value": "_id"
          },
          modelMap: "landmark",
          colAttrs: {}
        },
        {
          type: "textArea",
          colAttrs: {},
          attrs: {
            label: "Address line 1"
          },
          modelMap: "addressLine1"
        },
        {
          type: "textArea",
          attrs: {
            label: "Address line 2"
          },
          colAttrs: {},
          modelMap: "addressLine2"
        }
      ];
    },
    propertyImageCards() {
      return [
        {
          text: this.$t("landlord.uploadThumbnailforProperty"),
          cardHeight: "200px",
          btnText: this.$t("landlord.addThumbanails"),
          modelMap: "thumbnail",
          attrs: {
            multiple: false
          },
          on: {
            click: () => {
              const element = document.getElementById("thumbnail");
              element.click();
            }
          },
          images: this.viewOnlyImages.propertyThumbnail,
          imageCardOn: {
            click: () => {
              this.propertyImageVariables.thumbnail = [];
            }
          }
        },
        {
          text: this.$t("landlord.uploadPropertyPhotos"),
          cardHeight: "200px",
          btnText: this.$t("landlord.addPhotos"),
          modelMap: "images",
          attrs: {
            multiple: true
          },
          on: {
            click: () => {
              const element = document.getElementById("images");
              element.click();
            }
          },
          images: this.viewOnlyImages.propertyImages,
          imageCardOn: {
            click: () => {
              this.propertyImageVariables.images = [];
            }
          }
        }
      ];
    }
  },
  apollo: {
    getAllPropertyTypes() {
      return {
        query: require("@/landlord/api/queries/get_property_types.graphql"),
        variables: {
          limit: 50,
          skip: 0,
          rentType: this.variables.rentType
        },
        result({ data }) {
          this.propertyTypes = data.getPropertyTypes.PropertyTypes;
        },
        skip() {
          return !this.variables.rentType;
        }
      };
    },
    getFacilities() {
      return {
        query: require("@/landlord/api/queries/get_facilities.graphql"),
        variables: {
          limit: 50,
          skip: 0
        },
        result({ data }) {
          this.facilities = data.getFacilities.facilities;
        }
      };
    },
    getAllCurrency() {
      return {
        query: require("@/landlord/api/queries/get_all_currency.graphql"),
        variables: {
          input: {
            limit: 20,
            skip: 0
          }
        },
        result({ data }) {
          this.currencyTypes = data.getAllCurrency.currencies;
        }
      };
    },
    getAreasUnderCountry() {
      return {
        query: require("@/landlord/api/queries/get_areas_under_country.graphql"),
        variables: () => ({
          latitude: String(this.variables.latitude),
          longitude: String(this.variables.longitude)
        }),
        result({ data }) {
          this.areas = data.getAreasUnderCountry;
        },
        skip() {
          return !(this.variables.latitude && this.variables.longitude);
        }
      };
    },
    getBlockByArea() {
      return {
        query: require("@/landlord/api/queries/get_blocks_by_area.graphql"),
        variables: () => ({
          limit: 50,
          skip: 0,
          areaId: this.locationVariables.area
        }),
        result({ data }) {
          this.blocks = data.getBlockByArea.blocks.filter(
            block => block.isActive
          );
        },
        skip() {
          return !this.locationVariables.area;
        }
      };
    },
    getPropertyById() {
      return {
        query: require("@/landlord/api/queries/get_property_by_id.graphql"),
        variables: {
          id: this.propertyId
        },
        result({ data }) {
          const propertyDetails = data.getPropertyById;
          this.variables = {
            propertyType: propertyDetails.propertyType._id,
            rentType: "PER_MONTH",
            nameEn: propertyDetails.nameEn,
            nameAr: propertyDetails.nameAr,
            latitude: propertyDetails.latitude,
            longitude: propertyDetails.longitude,
            descriptionEn: propertyDetails.descriptionEn,
            descriptionAr: propertyDetails.descriptionAr,
            policiesEn: propertyDetails.policiesEn,
            policiesAr: propertyDetails.policiesAr,
            helthHygeineEn: propertyDetails.helthHygeineEn,
            helthHygeineAr: propertyDetails.helthHygeineAr,
            facilities: propertyDetails.facilities.map(
              facility => facility._id
            ),
            landmark: propertyDetails.landmark,
            isTaxInclusive: propertyDetails.isTaxInclusive,
            tax: propertyDetails.tax,
            currency: propertyDetails.currency,
            isCurrentAndWaterBillIncluded:
              propertyDetails.isCurrentAndWaterBillIncluded
          };
          this.locationVariables = {
            area: propertyDetails.address.area._id,
            block: propertyDetails.address.block._id,
            addressLine1: propertyDetails.address.addressLine1,
            addressLine2: propertyDetails.address.addressLine2,
            landmark: propertyDetails.landmark._id
          };
          this.variables.latitude = propertyDetails.latitude;
          this.variables.longitude = propertyDetails.longitude;
          if (propertyDetails.thumbanails[0]) {
            this.propertyImageVariables.thumbnail = [
              `${this.$store.getters.imageBaseUrl}/` +
                propertyDetails.thumbanails[0]
            ];
          }
          if (propertyDetails.images.length > 0) {
            this.propertyImageVariables.images = propertyDetails.images.map(
              image => `${this.$store.getters.imageBaseUrl}/` + image
            );
          }
        },
        skip() {
          return !this.propertyId;
        }
      };
    },
    getLandlords() {
      this.loading = true;
      return {
        query: require("@/landlord/api/queries/landlord_query.graphql"),
        variables: () => {
          return {
            input: {
              limit: 10,
              skip: 0
            },
            filter: {
              searchKey: this.landlordSearchKey,
              sortBy: "",
              sortDesc: false
            }
          };
        },
        result({ data }) {
          this.landlordLists = data.getLandlords.landlords;
          this.landlordLists = this.landlordLists.filter(landlord => {
            return (
              landlord.destinationId &&
              !landlord.isContractEnded &&
              landlord.user.active
            );
          });
        },
        skip() {
          return !this.manualLandlordSelection;
        }
      };
    },
    getLandmarkByArea() {
      return {
        query: require("@/landlord/api/queries/get_landmark_by_area.graphql"),
        variables: () => ({
          limit: 50,
          skip: 0,
          areaId: this.locationVariables.area
        }),
        result({ data }) {
          this.landmarks = data.getLandmarkByArea.landmarks;
        },
        skip() {
          return !this.locationVariables.area;
        }
      };
    }
  }
};
</script>

<style scoped></style>
